/**
 * Drill-through — every chart element is a link to the item list, pre-filtered
 * to exactly what was clicked (roadmap RP-7: "reports are never dead ends").
 *
 * The item list reads a `pql` parameter and opens in statement mode with that
 * predicate (see item-list/hooks/useStatementQuery.ts), so a drill link is a
 * PQL statement. Clauses are joined with AND; a null clause is skipped, which
 * is how a report adds its range only when one is set.
 *
 * Field names here are the statement aliases the parser accepts (`category`,
 * `status`, `queue`, `cycle`, `created`, …) — see FIELD_ALIASES in the shared
 * PQL catalog. Values are quoted by the same formatter the printer uses, so a
 * value with a quote or a space in it still parses.
 */

import { formatValue } from '../../../../lib/pql';

export const ITEMS_PATH = '/prioritization/items';

/** `field = "value"`, or `field IS EMPTY` when the value is missing. */
export function clause(field: string, value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') return `${field} IS EMPTY`;
  if (typeof value === 'boolean') return `${field} = ${value ? 'true' : 'false'}`;
  if (typeof value === 'number') return `${field} = ${value}`;
  return `${field} = ${formatValue(value)}`;
}

/** `field IN ("a", "b")`; one value is a plain clause, none is null (no narrowing). */
export function inClause(field: string, values: ReadonlyArray<string>): string | null {
  const unique = [...new Set(values.filter((v) => v !== ''))];
  if (unique.length === 0) return null;
  if (unique.length === 1) return clause(field, unique[0]);
  return `${field} IN (${unique.map((v) => formatValue(v)).join(', ')})`;
}

/** Join clauses into one statement; nulls are dropped. Empty → ''. */
export function joinClauses(clauses: ReadonlyArray<string | null | undefined>): string {
  return clauses.filter((c): c is string => !!c && c.trim().length > 0).join(' AND ');
}

/** The item list address for a statement. An empty statement opens the plain list. */
export function itemsHrefForStatement(statement: string): string {
  const s = statement.trim();
  return s ? `${ITEMS_PATH}?pql=${encodeURIComponent(s)}` : ITEMS_PATH;
}

/** One call for the common case: clauses in, address out. */
export function drillToItems(...clauses: Array<string | null | undefined>): string {
  return itemsHrefForStatement(joinClauses(clauses));
}
